import { useState } from 'react';
import { AlertCircle, Lock } from 'lucide-react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedCardInstrument } from '@/lib/paycom/types';
import { detectCardBrand } from '@/lib/card-formatting';
import { Button } from '@/components/ui';
import { NewCardForm, type NewCardFormData } from '@/components/features/Deposit/NewCardForm';
import { PaymentMethodsSkeleton } from '@/components/features/Deposit/PaymentMethodsSkeleton';

interface Props {
  /** Called with the freshly-tokenized card. Caller is responsible for
   *  refreshing the picker's saved-card list (or appending locally). */
  onSaved: (card: SavedCardInstrument) => void;
  onCancel: () => void;
}

// Sits inside the same PayComProvider tree as the rest of the withdraw
// flow (setup-session-backed). Drives `checkout.headless('card', …)` to
// tokenize a new card and returns the resulting `pm_card_*` token + brand
// + last4 to the parent. No api call here — submission of the actual
// withdrawal carries the token snapshot in the POST /withdrawals body.
export function WithdrawalCardSetup({ onSaved, onCancel }: Props) {
  const { state: payCom } = usePayComContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(form: NewCardFormData) {
    if (!payCom.checkoutObject) return;
    const checkout = payCom.checkoutObject;

    setError(null);
    setSubmitting(true);

    const cleanedNumber = form.cardNumber.replace(/\s/g, '');
    const brand = detectCardBrand(cleanedNumber);
    const last4 = cleanedNumber.slice(-4);

    try {
      const sourceId = await tokenize(checkout, form);
      onSaved({
        id: sourceId,
        bin: cleanedNumber.slice(0, 6),
        last4,
        brand,
        hasCVV: true,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Could not save the card. Please try again.';
      setError(errorMessage);
      setSubmitting(false);
    }
  }

  if (payCom.isInitializing) {
    return <PaymentMethodsSkeleton />;
  }
  if (payCom.isFailed || !payCom.checkoutObject) {
    return (
      <div className="flex flex-col gap-4">
        <div
          role="alert"
          className="flex items-start gap-3 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] p-4"
        >
          <AlertCircle className="size-5 text-danger-alt shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary">Couldn't load the card form</p>
            <p className="text-xs text-muted mt-1">{payCom.error ?? 'Try again in a moment.'}</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={onCancel}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <NewCardForm
        isSubmitting={submitting}
        // Withdraw always persists the tokenized card into the Pay.com
        // vault so it carries across sessions and surfaces on both the
        // deposit and withdraw screens.
        savePaymentMethod
        onSavePaymentMethodChange={() => undefined}
        onBack={onCancel}
        onSubmit={handleSubmit}
        submitLabel="Save card"
      />
      {error && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] px-3 py-2.5"
        >
          <AlertCircle className="size-4 text-danger-alt shrink-0 mt-0.5" />
          <p className="text-sm text-danger-alt">{error}</p>
        </div>
      )}
      <p className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-muted pt-1">
        <Lock className="size-3" />
        Secured by Pay.com · PCI-compliant
      </p>
    </div>
  );
}

const SETUP_TIMEOUT_MS = 30_000;

// Drives the SDK headless tokenize call and resolves with the source id
// (`pm_card_*`). Pay.com's SDK exposes the new source through several
// surfaces depending on version + mode; race them all so we resolve on
// whichever lands first:
//   1. The resolved value of `checkout.headless(...)`
//   2. An emitted `SETUP_SUCCESS` event
//   3. A fresh `getExistingSources()` call after headless resolves
async function tokenize(
  checkout: NonNullable<ReturnType<typeof usePayComContext>['state']['checkoutObject']>,
  form: NewCardFormData,
): Promise<string> {
  const cleanedNumber = form.cardNumber.replace(/\s/g, '');
  const last4 = cleanedNumber.slice(-4);

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const settleSuccess = (sourceId: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(sourceId);
    };
    const settleFailure = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    const timeoutId = window.setTimeout(
      () => settleFailure(new Error('Card tokenization timed out. Please try again.')),
      SETUP_TIMEOUT_MS,
    );

    const onSuccess = (...args: unknown[]) => {
      const sourceId = extractSourceId(args);
      if (sourceId) settleSuccess(sourceId);
    };
    const onFailure = (...args: unknown[]) => {
      settleFailure(new Error(extractFailureMessage(args) ?? 'Card was rejected by the issuer.'));
    };
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      try {
        checkout.removeListener(checkout.EVENT_TYPES.SETUP_SUCCESS, onSuccess);
        checkout.removeListener(checkout.EVENT_TYPES.SETUP_FAILURE, onFailure);
      } catch {
        // Listener may not be registered yet; safe to ignore.
      }
    };

    checkout.once(checkout.EVENT_TYPES.SETUP_SUCCESS, onSuccess);
    checkout.once(checkout.EVENT_TYPES.SETUP_FAILURE, onFailure);

    checkout
      .headless(
        'card',
        {
          number: cleanedNumber,
          cvv: form.cvv,
          expirationMonth: form.expiryMonth,
          expirationYear: form.expiryYear,
          name: form.cardholderName,
        },
        undefined,
        { saveSourceForFutureUse: true },
      )
      .then(async (result) => {
        const direct = extractSourceId([result]);
        if (direct) {
          settleSuccess(direct);
          return;
        }
        try {
          const sources = await checkout.getExistingSources();
          const match = sources.find((s) => s?.type === 'card' && s.card?.last4 === last4);
          if (match?.id) {
            settleSuccess(match.id);
            return;
          }
        } catch {
          // Fall through to SETUP_SUCCESS event or timeout.
        }
      })
      .catch((err: unknown) => {
        settleFailure(err instanceof Error ? err : new Error('Card tokenization failed.'));
      });
  });
}

function extractSourceId(args: unknown[]): string | null {
  for (const arg of args) {
    if (!arg) continue;
    if (typeof arg === 'string' && arg.startsWith('pm_')) return arg;
    if (typeof arg !== 'object') continue;
    const obj = arg as Record<string, unknown>;
    if (typeof obj.sourceId === 'string' && obj.sourceId) return obj.sourceId;
    if (typeof obj.id === 'string' && obj.id.startsWith('pm_')) return obj.id;
    const pm = obj.paymentMethod as Record<string, unknown> | undefined;
    if (pm && typeof pm.id === 'string' && pm.id.startsWith('pm_')) return pm.id;
    const src = obj.source as Record<string, unknown> | undefined;
    if (src && typeof src.id === 'string' && src.id.startsWith('pm_')) return src.id;
  }
  return null;
}

function extractFailureMessage(args: unknown[]): string | null {
  for (const arg of args) {
    if (!arg) continue;
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'object') {
      const obj = arg as Record<string, unknown>;
      if (typeof obj.message === 'string') return obj.message;
      if (typeof obj.reason === 'string') return obj.reason;
    }
  }
  return null;
}
